import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react'
import { parseISO, format } from 'date-fns'
import { useTimeline, useDayDetail } from '../api/hooks'
import type { DayItem } from '../api/types'
import { DayDetail } from '../components/sessions/DayDetail'
import { SessionCalendar } from '../components/sessions/SessionCalendar'
import { TOKENS } from '../components/ui/dashboard-primitives'
import { AsyncPanel, PanelSkeleton, Shimmer } from '../components/ui/AsyncPanel'
import { PageError } from '../components/errors/PanelError'
import { panelOf, panelState } from '../lib/queryState'
import { cn } from '../lib/utils'

function scrollToTop() {
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' })
}

/** The one switch that arms the j/k keys, remembered on this device. Off until
 *  someone turns it on: a single-letter shortcut that cannot be turned off
 *  fails WCAG 2.1.4, and one that is on by default takes a key from anybody
 *  who dictates or types with a switch device. */
const STEP_KEYS_STORAGE = 'groundwork_sessions_step_keys'

function readStepKeys(): boolean {
  try {
    return localStorage.getItem(STEP_KEYS_STORAGE) === 'on'
  } catch {
    return false
  }
}

function writeStepKeys(on: boolean) {
  try {
    localStorage.setItem(STEP_KEYS_STORAGE, on ? 'on' : 'off')
  } catch {
    // Private window or blocked storage: the switch still works for this visit.
  }
}

/** Where a typed letter belongs to something else: a field, or a widget that
 *  searches by the letters typed on it. A BUTTON is not on this list. `j` and
 *  `k` are not keys a button takes, and in Chromium a button keeps focus after
 *  a mouse click, so excluding it would stop the keys working right after
 *  someone used Earlier or Later. */
const OWNS_LETTERS =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], ' +
  '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], ' +
  '[role="combobox"], [role="textbox"]'

function ownsLetters(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(OWNS_LETTERS) !== null
}

/** Rail of recent training days, each marked for the parts it contains. */
function DayRail({
  items,
  activeIndex,
  onSelect,
}: {
  items: DayItem[]
  activeIndex: number
  onSelect: (index: number) => void
}) {
  const recent = useMemo(() => items.slice(0, 10), [items])
  if (recent.length < 2) return null

  return (
    <div
      role="group"
      aria-label="Recent training days"
      className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1"
    >
      {recent.map((item, i) => {
        const isActive = i === activeIndex
        const d = parseISO(item.date)
        const parts = [
          item.cardio_count > 0 ? 'Cardio' : null,
          item.has_milon ? 'Strength' : null,
          item.has_free_weights ? 'Free Weights' : null,
        ]
          .filter(Boolean)
          .join(' + ')
        return (
          <button
            key={item.date}
            type="button"
            onClick={() => onSelect(i)}
            aria-pressed={isActive}
            aria-label={`Open ${parts || 'session'} from ${format(d, 'EEEE, MMMM d, yyyy')}`}
            className="group inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 transition-all duration-200 outline-none focus-visible:ring-2 min-h-11 active:scale-[0.97]"
            style={
              {
                backgroundColor: isActive ? TOKENS.creamHairline : 'transparent',
                border: `1px solid ${isActive ? TOKENS.amberGlow : TOKENS.creamBorder}`,
                fontFamily: 'var(--font-mono)',
                fontSize: '11px',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                ['--tw-ring-color' as never]: TOKENS.amberEmphasis,
              } as React.CSSProperties
            }
          >
            <span className="inline-flex items-center gap-1 shrink-0" aria-hidden>
              {item.has_milon && (
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: TOKENS.amber, opacity: isActive ? 1 : 0.6 }}
                />
              )}
              {item.cardio_count > 0 && (
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    border: `1px solid ${TOKENS.amber}`,
                    backgroundColor: 'transparent',
                    opacity: isActive ? 1 : 0.55,
                  }}
                />
              )}
              {item.has_free_weights && (
                <span
                  style={{
                    display: 'inline-block',
                    width: 6,
                    height: 6,
                    borderRadius: 2,
                    // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
                    backgroundColor: 'rgba(232,197,160,0.7)',
                    opacity: isActive ? 1 : 0.55,
                  }}
                />
              )}
            </span>
            <span style={{ color: isActive ? TOKENS.cream : TOKENS.creamSoft }} className="tabular-nums">
              {format(d, 'MMM d')}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function NavBtn({
  label,
  disabled,
  onClick,
  children,
  buttonRef,
}: {
  label: string
  disabled?: boolean
  onClick: () => void
  children: React.ReactNode
  buttonRef?: React.RefObject<HTMLButtonElement | null>
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="inline-flex items-center gap-2 rounded-xl px-4 py-2 min-h-[44px] transition-colors duration-200 outline-none focus-visible:ring-2 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98]"
      style={
        {
          backgroundColor: TOKENS.creamHairline,
          border: `1px solid ${TOKENS.creamBorder}`,
          color: 'var(--text-cream-soft)',
          fontFamily: 'var(--font-mono)',
          ['--tw-ring-color' as never]: TOKENS.amberEmphasis,
        } as React.CSSProperties
      }
      onMouseEnter={(e) => {
        if (disabled) return
        // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
        e.currentTarget.style.backgroundColor = 'rgba(232, 197, 160, 0.1)'
        // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
        e.currentTarget.style.borderColor = 'rgba(232, 197, 160, 0.32)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = TOKENS.creamHairline
        e.currentTarget.style.borderColor = TOKENS.creamBorder
      }}
    >
      {children}
    </button>
  )
}

/** Arms the j/k keys. Shown where a keyboard is likely (`any-pointer-fine`: a
 *  mouse or trackpad is attached, which an iPad with a keyboard has and a bare
 *  phone does not), because a switch for keys is noise on a phone. The label
 *  says what ON does; the keys themselves work wherever the switch is on. */
function StepKeysSwitch({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      title="Press J for the earlier day and K for the later one"
      className="hidden any-pointer-fine:inline-flex items-center gap-2 rounded-full px-3 py-1.5 transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-(--accent-amber) active:scale-[0.97]"
      style={
        {
          backgroundColor: on ? TOKENS.creamHairline : 'transparent',
          border: `1px solid ${on ? TOKENS.amberGlow : TOKENS.creamBorder}`,
          // The label is cream either way: it is the DOT and the tint that say
          // on or off, and the dimmer text was 3.1:1 against the tan peak.
          color: TOKENS.cream,
          fontFamily: 'var(--font-mono)',
          fontSize: '11px',
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
        } as React.CSSProperties
      }
    >
      <span
        aria-hidden
        className="w-1.5 h-1.5 rounded-full"
        style={{
          backgroundColor: on ? TOKENS.amber : 'transparent',
          border: `1px solid ${TOKENS.amber}`,
          opacity: on ? 1 : 0.55,
        }}
      />
      Step with J / K
    </button>
  )
}

function SessionsSkeleton({ label }: { label: string }) {
  return (
    <PanelSkeleton label={label}>
      <div className="max-w-[1200px] mx-auto pt-10 lg:pt-14 pb-20 flex flex-col gap-8">
        <Shimmer height="0.75rem" width="18rem" />
        <Shimmer height="4rem" width="22rem" />
        <Shimmer height="1px" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex flex-col gap-3">
              <Shimmer height="2.5rem" width="60%" />
              <Shimmer height="0.75rem" width="80%" />
            </div>
          ))}
        </div>
        <Shimmer height="14rem" />
      </div>
    </PanelSkeleton>
  )
}

/** The page's own heading sits outside the body because the body has four
 *  early returns — a skeleton, an error, an empty state, and the real thing —
 *  and two of them used to render no heading at all. Emitting it here makes it
 *  impossible for a loading, failed or empty page to go unnamed. */
export default function Sessions() {
  return (
    <>
      <h1 className="sr-only">Sessions</h1>
      <SessionsBody />
    </>
  )
}

function SessionsBody() {
  const timelineQuery = useTimeline()
  const timeline = timelineQuery.data
  const [activeIndex, setActiveIndex] = useState(0)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [stepKeys, setStepKeys] = useState(readStepKeys)
  /** What a key-driven step says aloud: the page changes under a screen reader
   *  without moving focus, so nothing else announces it. */
  const [announce, setAnnounce] = useState('')
  const calendarTriggerRef = useRef<HTMLButtonElement>(null)

  const items = useMemo(() => timeline?.items ?? [], [timeline])
  const availableDates = useMemo(() => items.map((item) => item.date), [items])

  // If the list shrank below the active index, snap back to the first item
  // during render (React-recommended over resetting via an effect).
  if (items.length && activeIndex > items.length - 1) {
    setActiveIndex(0)
  }

  const selected = items[activeIndex] ?? null

  const dayQuery = useDayDetail(selected?.date ?? null)
  const { data: day, isFetching } = dayQuery
  const dayPanel = panelOf([dayQuery], day != null)

  // date-desc order: higher index = EARLIER day, lower index = LATER day.
  const goTo = useCallback(
    (index: number) => {
      if (index < 0 || index > items.length - 1) return
      setActiveIndex(index)
      scrollToTop()
    },
    [items.length],
  )

  const onSelectDate = useCallback(
    (date: string) => {
      const index = items.findIndex((item) => item.date === date)
      if (index >= 0) goTo(index)
    },
    [items, goTo],
  )

  // j and k, and only those, and only once the switch is on (BU-8). The arrow
  // keys are NOT bound here: they scroll the page, and each press used to change
  // the day and jump to the top instead. A key is left alone when something
  // already handled it, when a modifier says it is a shortcut of the browser's,
  // while the calendar is open, and where the letter belongs to a field.
  useEffect(() => {
    if (!stepKeys || isCalendarOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return
      if (e.key !== 'j' && e.key !== 'k') return
      if (ownsLetters(e.target)) return
      const next = e.key === 'j' ? activeIndex + 1 : activeIndex - 1
      goTo(next)
      const day = items[next]
      if (day) setAnnounce(`Showing ${format(parseISO(day.date), 'EEEE, MMMM d, yyyy')}`)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [stepKeys, isCalendarOpen, activeIndex, goTo, items])

  // The timeline is the page's only source, so a page that cannot draw says so
  // once, at page scope, as the dashboard does when all of it fails (Spec 2 D5,
  // #172). A paused request counts as failed: waiting on `!timeline` held the
  // skeleton forever.
  const page = panelState([timelineQuery], items.length > 0)

  if (page === 'loading') return <SessionsSkeleton label="Loading your sessions" />

  if (page === 'error') return <PageError subject="Your sessions" sources={[timelineQuery]} />

  if (page === 'empty') {
    return (
      <div className="max-w-[1200px] mx-auto pt-20 pb-20 text-center">
        <p
          className="text-[11px] tracking-[0.28em] uppercase"
          style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
        >
          No sessions yet
        </p>
        <p className="mt-3 text-sm" style={{ fontFamily: 'var(--font-body)', color: TOKENS.creamFaint }}>
          Run a sync to pull your training history.
        </p>
      </div>
    )
  }

  return (
    <div className="pb-16" style={{ color: TOKENS.cream }}>
      <div className="max-w-[1200px] mx-auto flex flex-wrap items-center justify-between gap-4 pt-10 lg:pt-14">
        <DayRail items={items} activeIndex={activeIndex} onSelect={goTo} />
        {/* Below sm the three buttons drop their words and keep the icon (each keeps its aria-label):
            with the words the row was 3 x ~110px and pushed the page to 387px on a 375px phone. Shipping
            apps step days with bare chevrons on a phone (Ladder, Equinox+, Withings, MacroFactor). */}
        <div className="flex items-center gap-2 ml-auto">
          <p className="sr-only" role="status" aria-live="polite">
            {announce}
          </p>
          <StepKeysSwitch
            on={stepKeys}
            onToggle={() => {
              const next = !stepKeys
              setStepKeys(next)
              writeStepKeys(next)
            }}
          />
          <NavBtn label="Open session calendar" onClick={() => setIsCalendarOpen(true)} buttonRef={calendarTriggerRef}>
            <CalendarIcon className="w-4 h-4" strokeWidth={2} />
            <span className="hidden sm:inline text-[11px] tracking-[0.22em] uppercase">Calendar</span>
          </NavBtn>
          <NavBtn label="Earlier day" disabled={activeIndex >= items.length - 1} onClick={() => goTo(activeIndex + 1)}>
            <ChevronLeft className="w-4 h-4" strokeWidth={2} />
            <span className="hidden sm:inline text-[11px] tracking-[0.22em] uppercase">Earlier</span>
          </NavBtn>
          <NavBtn label="Later day" disabled={activeIndex <= 0} onClick={() => goTo(activeIndex - 1)}>
            <span className="hidden sm:inline text-[11px] tracking-[0.22em] uppercase">Later</span>
            <ChevronRight className="w-4 h-4" strokeWidth={2} />
          </NavBtn>
        </div>
      </div>

      {/* The error note carries no column or top padding of its own; the
          day detail and its skeleton do. */}
      <div className={cn(dayPanel.state === 'error' && 'max-w-[1200px] mx-auto pt-10 lg:pt-14')}>
        <AsyncPanel
          sources={dayPanel.sources}
          hasContent={dayPanel.hasContent}
          subject="This training day"
          skeleton={<SessionsSkeleton label="Loading this training day" />}
        >
          {() => day && <DayDetail day={day} isFetching={isFetching} />}
        </AsyncPanel>
      </div>

      <SessionCalendar
        isOpen={isCalendarOpen}
        onClose={() => setIsCalendarOpen(false)}
        availableDates={availableDates}
        selectedDate={selected?.date ?? ''}
        onSelectDate={onSelectDate}
        returnFocusRef={calendarTriggerRef}
      />
    </div>
  )
}
